// ========================================
// 効果ヘルパー関数
// カード固有処理で使用する共通処理
// ========================================

import { statusEffectEngine, getStatusDisplayName, STATUS_EFFECT_TYPES } from './statusEffects';
import { TRIGGER_TYPES } from './triggerTypes';
import {
  fireTrigger,
  unregisterCardTriggers,
  fireLeaveFieldTrigger,
} from './triggerEngine';
import type { TriggerContext } from './triggerEngine';
import { continuousEffectEngine } from './continuousEffects';
import { handleLinkBreak, hasMashouheki, FieldArray } from './keywordAbilities';
import type {
  EffectContext,
  PlayerContext,
  CardData,
  CardWithId,
  MonsterInstance,
  HandCard,
  StatusEffectType,
  PlayerNumber,
} from '../types';

// ========================================
// 【魔障壁】キーワード能力 保護チェック
// ========================================

/** 魔法効果コンテキスト */
interface MagicEffectContext extends EffectContext {
  isMagicEffect?: boolean;
  magicCaster?: PlayerNumber;
}

/** 状態異常付与結果 */
interface ApplyStatusResult {
  success: boolean;
  message?: string;
}

/** ターン開始処理結果 */
interface ProcessTurnStartResult {
  monster: MonsterInstance;
  removedEffects: Array<{ type: StatusEffectType }>;
  parasiteAtkReduction: number;
}

/** 相手エンドフェイズ処理結果 */
interface ProcessOpponentEndPhaseResult {
  monster: MonsterInstance;
  parasiteEffectNegationRemoved: boolean;
}

/**
 * 【魔障壁】による保護チェック
 * 魔障壁を持つモンスターは「相手の魔法の効果を受けない」
 *
 * @param monster - 対象モンスター
 * @param context - 効果コンテキスト（isMagicEffect, magicCaster が必要）
 * @returns 保護されているか（true = 効果を受けない）
 */
export const isMashouhekiProtected = (monster: MonsterInstance | null, context: MagicEffectContext): boolean => {
  if (!monster) return false;

  // 魔法効果でなければ保護されない
  if (!context.isMagicEffect) return false;

  // 【魔障壁】を持っていなければ保護されない
  if (!hasMashouheki(monster)) return false;

  // 「相手の」魔法のみ保護（自分の魔法は受ける）
  // monster.owner と magicCaster が異なれば相手の魔法
  const monsterOwner = (monster as MonsterInstance & { owner?: PlayerNumber }).owner;
  const magicCaster = context.magicCaster;

  // owner が未設定の場合は保護しない（安全側に倒す）
  if (monsterOwner === undefined || magicCaster === undefined) return false;

  return monsterOwner !== magicCaster;
};

/**
 * 魔障壁保護のログを出力
 * @param monster - 保護されたモンスター
 * @param addLog - ログ関数
 */
export const logMashouhekiProtection = (monster: MonsterInstance, addLog: EffectContext['addLog']): void => {
  if (addLog) {
    addLog(`【魔障壁】${monster.name}は魔法の効果を受けない！`, 'info');
  }
};

// ========================================
// プレイヤーコンテキスト抽象化
// currentPlayer判定を一元化し、「自分/相手」で扱えるようにする
// ========================================

/**
 * currentPlayerに基づいて「自分/相手」に抽象化したプロパティを返す
 * @param context - ゲームコンテキスト
 * @returns プレイヤー抽象化されたプロパティ
 *
 * @example
 * // Before（旧パターン）:
 * const { currentPlayer, p1Field, p2Field, setP1Field, setP2Field } = context;
 * const myField = currentPlayer === 1 ? p1Field : p2Field;
 * const opponentField = currentPlayer === 1 ? p2Field : p1Field;
 *
 * // After（新パターン）:
 * const { myField, opponentField, setMyField } = getPlayerContext(context);
 */
export const getPlayerContext = (context: (EffectContext | TriggerContext) & Record<string, any>): PlayerContext & Record<string, any> => {
  const { currentPlayer } = context;
  const isP1 = currentPlayer === 1;

  // Note: TriggerContext extends Partial<EffectContext>, so some properties may be undefined.
  // We use non-null assertions (!) as getPlayerContext is called in contexts where these exist.
  return {
    // === フィールド ===
    myField: (isP1 ? context.p1Field : context.p2Field)!,
    opponentField: (isP1 ? context.p2Field : context.p1Field)!,
    setMyField: (isP1 ? context.setP1Field : context.setP2Field)!,
    setOpponentField: (isP1 ? context.setP2Field : context.setP1Field)!,

    // === 手札 ===
    myHand: (isP1 ? context.p1Hand : context.p2Hand)!,
    opponentHand: (isP1 ? context.p2Hand : context.p1Hand)!,
    setMyHand: (isP1 ? context.setP1Hand : context.setP2Hand)!,
    setOpponentHand: (isP1 ? context.setP2Hand : context.setP1Hand)!,

    // === デッキ ===
    myDeck: (isP1 ? context.p1Deck : context.p2Deck)!,
    opponentDeck: (isP1 ? context.p2Deck : context.p1Deck)!,
    setMyDeck: (isP1 ? context.setP1Deck : context.setP2Deck)!,
    setOpponentDeck: (isP1 ? context.setP2Deck : context.setP1Deck)!,

    // === 墓地 ===
    myGraveyard: (isP1 ? context.p1Graveyard : context.p2Graveyard)!,
    opponentGraveyard: (isP1 ? context.p2Graveyard : context.p1Graveyard)!,
    setMyGraveyard: (isP1 ? context.setP1Graveyard : context.setP2Graveyard)!,
    setOpponentGraveyard: (isP1 ? context.setP2Graveyard : context.setP1Graveyard)!,

    // === ライフ ===
    myLife: (isP1 ? context.p1Life : context.p2Life)!,
    opponentLife: (isP1 ? context.p2Life : context.p1Life)!,
    setMyLife: (isP1 ? context.setP1Life : context.setP2Life)!,
    setOpponentLife: (isP1 ? context.setP2Life : context.setP1Life)!,

    // === SP（アクティブ） ===
    myActiveSP: (isP1 ? context.p1ActiveSP : context.p2ActiveSP)!,
    opponentActiveSP: (isP1 ? context.p2ActiveSP : context.p1ActiveSP)!,
    setMyActiveSP: (isP1 ? context.setP1ActiveSP : context.setP2ActiveSP)!,
    setOpponentActiveSP: (isP1 ? context.setP2ActiveSP : context.setP1ActiveSP)!,

    // === SP（レスト） ===
    myRestedSP: (isP1 ? context.p1RestedSP : context.p2RestedSP)!,
    opponentRestedSP: (isP1 ? context.p2RestedSP : context.p1RestedSP)!,
    setMyRestedSP: (isP1 ? context.setP1RestedSP : context.setP2RestedSP)!,
    setOpponentRestedSP: (isP1 ? context.setP2RestedSP : context.setP1RestedSP)!,

    // === フィールドカード ===
    myFieldCard: (isP1 ? context.p1FieldCard : context.p2FieldCard)!,
    opponentFieldCard: (isP1 ? context.p2FieldCard : context.p1FieldCard)!,
    setMyFieldCard: (isP1 ? context.setP1FieldCard : context.setP2FieldCard)!,
    setOpponentFieldCard: (isP1 ? context.setP2FieldCard : context.setP1FieldCard)!,

    // === フェイズカード ===
    myPhaseCard: isP1 ? context.p1PhaseCard : context.p2PhaseCard,
    opponentPhaseCard: isP1 ? context.p2PhaseCard : context.p1PhaseCard,
    setMyPhaseCard: isP1 ? context.setP1PhaseCard : context.setP2PhaseCard,
    setOpponentPhaseCard: isP1 ? context.setP2PhaseCard : context.setP1PhaseCard,

    // === 状態異常（プレイヤー） ===
    myStatusEffects: isP1 ? context.p1StatusEffects : context.p2StatusEffects,
    opponentStatusEffects: isP1 ? context.p2StatusEffects : context.p1StatusEffects,
    setMyStatusEffects: isP1 ? context.setP1StatusEffects : context.setP2StatusEffects,
    setOpponentStatusEffects: isP1 ? context.setP2StatusEffects : context.setP1StatusEffects,

    // === ユーティリティ ===
    isP1,                 // currentPlayer === 1
    currentPlayer,        // 元の値（1 or 2）
  };
};

// ========================================
// カード効果ヘルパー関数
// ========================================

/**
 * デッキの上からカードを墓地に送る（ミル）
 * @param context - ゲームコンテキスト
 * @param count - 枚数
 * @returns 墓地に送ったカード配列
 */
export const millDeck = (context: EffectContext | TriggerContext, count: number = 1): CardWithId[] => {
  const { addLog } = context;
  const { myDeck, setMyDeck, setMyGraveyard } = getPlayerContext(context);

  if (!myDeck || myDeck.length < count) {
    addLog(`デッキが足りません（残り${myDeck?.length ?? 0}枚）`, 'info');
    return [];
  }

  const milledCards = myDeck.slice(0, count);
  setMyDeck((prev: any) => prev.slice(count));
  setMyGraveyard((prev: any) => [...prev, ...milledCards]);

  milledCards.forEach(card => {
    addLog(`デッキから「${card.name}」を墓地に送った`, 'info');
  });

  return milledCards;
};

/**
 * 相手のデッキの上からカードを墓地に送る
 * @param context - ゲームコンテキスト
 * @param count - 枚数
 * @returns 墓地に送ったカード配列
 */
export const millOpponentDeck = (context: EffectContext | TriggerContext, count: number = 1): CardWithId[] => {
  const { addLog } = context;
  const { opponentDeck, setOpponentDeck, setOpponentGraveyard } = getPlayerContext(context);

  if (!opponentDeck || opponentDeck.length < count) {
    addLog(`相手のデッキが足りません（残り${opponentDeck?.length ?? 0}枚）`, 'info');
    return [];
  }

  const milledCards = opponentDeck.slice(0, count);
  setOpponentDeck((prev: any) => prev.slice(count));
  setOpponentGraveyard((prev: any) => [...prev, ...milledCards]);

  milledCards.forEach(card => {
    addLog(`相手のデッキから「${card.name}」を墓地に送った`, 'info');
  });

  return milledCards;
};

/**
 * 属性をチェック
 * @param card - カードオブジェクト
 * @param attribute - チェックする属性
 * @returns boolean
 */
export const checkAttribute = (card: CardData, attribute: string): boolean => {
  return card.attribute === attribute;
};

/** 破壊防止結果 */
interface DestroyPreventionResult {
  prevented: boolean;
  newHp?: number;
}

/** フィールドカード破壊防止コンテキスト */
interface FieldCardDestroyPreventionContext extends EffectContext {
  // setP1FieldCard, setP2FieldCard are used from EffectContext & additional properties
}

/**
 * フィールドカードによる破壊防止チェック
 * @param context - ゲームコンテキスト
 * @param monster - 破壊されようとしているモンスター
 * @param ownerPlayer - モンスターのオーナー (1 or 2)
 * @returns { prevented: boolean, newHp?: number } 防止された場合の結果
 */
export const checkFieldCardDestroyPrevention = (
  context: FieldCardDestroyPreventionContext,
  monster: MonsterInstance,
  ownerPlayer: PlayerNumber
): DestroyPreventionResult | null => {
  const {
    p1FieldCard, p2FieldCard,
    p1ActiveSP, p2ActiveSP,
    setP1ActiveSP, setP2ActiveSP,
    setP1Field, setP2Field,
    p1Field, p2Field,
    addLog,
  } = context;

  // オーナーのフィールドカードを取得
  const fieldCard = ownerPlayer === 1 ? p1FieldCard : p2FieldCard;
  const activeSP = ownerPlayer === 1 ? p1ActiveSP : p2ActiveSP;
  const setActiveSP = ownerPlayer === 1 ? setP1ActiveSP : setP2ActiveSP;
  const field = ownerPlayer === 1 ? p1Field : p2Field;
  const setField = ownerPlayer === 1 ? setP1Field : setP2Field;

  // C0000072: ルミナスの聖域
  // 【光属性モンスターが破壊される時】HPを1にして、代わりに自分のSPを1減らして守ることができる（1ターンに1度）
  if (fieldCard && fieldCard.id === 'C0000072') {
    // 光属性モンスターか確認
    if (monster.attribute !== '光') {
      return null;
    }

    // SPが1以上あるか確認
    if (activeSP < 1) {
      return null;
    }

    // 1ターンに1度の使用制限チェック（フィールドカードにフラグを設定）
    if ((fieldCard as CardData & { usedDestroyPreventionThisTurn?: boolean }).usedDestroyPreventionThisTurn) {
      return null;
    }

    // 効果発動: SP消費、HP1に設定、使用済みフラグ
    setActiveSP(prev => prev - 1);

    // フィールド上のモンスターのHPを1に更新
    setField(prev => {
      const newField = [...prev];
      const monsterIndex = newField.findIndex(m => m && m.uniqueId === monster.uniqueId);
      if (monsterIndex !== -1) {
        newField[monsterIndex] = { ...newField[monsterIndex]!, currentHp: 1 };
      }
      return newField;
    });

    // フィールドカードに使用済みフラグを設定
    const setFieldCard = ownerPlayer === 1 ? context.setP1FieldCard : context.setP2FieldCard;
    if (setFieldCard) {
      setFieldCard(prev => prev ? { ...prev, usedDestroyPreventionThisTurn: true } as CardData : prev);
    }

    addLog(`ルミナスの聖域の効果: ${monster.name}の破壊を防ぎ、HPを1にした！（SP1消費）`, 'info');

    return { prevented: true, newHp: 1 };
  }

  return null;
};

/**
 * モンスター破壊処理（トリガー発火、登録解除、墓地送り）
 * @param context - ゲームコンテキスト
 * @param monster - 破壊されるモンスター
 * @param slotIndex - フィールド上のスロットインデックス
 * @param ownerPlayer - モンスターのオーナー (1 or 2)
 * @returns 破壊が実行されたかどうか（falseの場合は破壊が防止された）
 */
export const handleMonsterDestruction = (
  context: EffectContext | TriggerContext,
  monster: MonsterInstance,
  slotIndex: number,
  ownerPlayer: PlayerNumber
): boolean => {
  // 破壊防止チェック（フィールドカード効果）
  const prevention = checkFieldCardDestroyPrevention(context as any, monster, ownerPlayer);
  if (prevention && prevention.prevented) {
    // 破壊は防止された（HP1に設定済み）
    return false;
  }
  const {
    setP1Life, setP2Life,
    setP1Field, setP2Field,
    setP1Hand, setP2Hand,
    setP1Deck, setP2Deck,
    setP1Graveyard, setP2Graveyard,
    p1Field, p2Field,
    p1Hand, p2Hand,
    p1Deck, p2Deck,
    p1Graveyard, p2Graveyard,
    p1Life, p2Life,
    addLog,
  } = context;

  // 破壊時トリガーを発火（破壊される前）
  const destroyContext = {
    currentPlayer: ownerPlayer,
    destroyedCard: monster,
    destroyedSlotIndex: slotIndex,
    setP1Life,
    setP2Life,
    setP1Field,
    setP2Field,
    setP1Hand,
    setP2Hand,
    setP1Deck,
    setP2Deck,
    setP1Graveyard,
    setP2Graveyard,
    p1Field,
    p2Field,
    p1Hand,
    p2Hand,
    p1Deck,
    p2Deck,
    p1Graveyard,
    p2Graveyard,
    p1Life,
    p2Life,
    addLog,
  };

  fireTrigger(TRIGGER_TYPES.ON_DESTROY_SELF, destroyContext as any);
  fireLeaveFieldTrigger(monster, destroyContext as any, 'destroy');

  // 【魂結】リンク解除処理
  const setFieldForLink = ownerPlayer === 1 ? setP1Field : setP2Field;
  handleLinkBreak(
    monster,
    setFieldForLink as (fn: (prev: FieldArray) => FieldArray) => void,
    addLog as (message: string, type?: string) => void
  );

  // トリガー登録を解除
  unregisterCardTriggers(monster.uniqueId);
  // 常時効果を解除
  continuousEffectEngine.unregister(monster.uniqueId);

  // フィールドから除去して墓地に送る
  const setField = ownerPlayer === 1 ? setP1Field : setP2Field;
  const setGraveyard = ownerPlayer === 1 ? setP1Graveyard : setP2Graveyard;

  setField!((prev: any) => {
    const newField = [...prev];
    newField[slotIndex] = null;
    return newField;
  });
  setGraveyard!((prev: any) => [...prev, monster]);
  addLog(`${monster.name}は破壊された！`, 'damage');

  // 寄生状態異常があれば寄生カードを寄生者の墓地に送る
  const parasiteInfo = statusEffectEngine.getParasiteInfo(monster) as { parasiteCard: CardWithId; parasiteOwner: PlayerNumber } | null;
  if (parasiteInfo) {
    const setParasiteGraveyard = parasiteInfo.parasiteOwner === 1 ? setP1Graveyard : setP2Graveyard;
    setParasiteGraveyard!((prev: any) => [...prev, parasiteInfo.parasiteCard]);
    addLog(`${parasiteInfo.parasiteCard.name}も墓地に送られた`, 'info');
  }

  return true; // 破壊が実行された
};

/** ダメージターゲット */
type DamageTarget = 'opponent' | 'self' | 'opponent_monster' | 'self_monster';

/** 条件付きダメージ用コンテキスト */
interface ConditionalDamageContext {
  currentPlayer: number;
  setP1Life?: (fn: (prev: number) => number) => void;
  setP2Life?: (fn: (prev: number) => number) => void;
  setP1Field?: (fn: (prev: any[]) => any[]) => void;
  setP2Field?: (fn: (prev: any[]) => any[]) => void;
  p1Field?: any[];
  p2Field?: any[];
  addLog: (message: string, type?: string) => void;
  monsterIndex?: number;
  [key: string]: any;
}

/**
 * 条件付きダメージ
 * @param context - ゲームコンテキスト
 * @param damage - ダメージ量
 * @param target - 'opponent', 'self', 'opponent_monster', 'self_monster'
 * @param targetIndex - モンスターのインデックス（オプション）
 * @returns 成功したかどうか
 */
export const conditionalDamage = (
  context: ConditionalDamageContext,
  damage: number,
  target: DamageTarget,
  targetIndex: number | null = null
): boolean => {
  const {
    currentPlayer,
    setP1Life, setP2Life,
    setP1Field, setP2Field,
    p1Field, p2Field,
    monsterIndex,
    addLog,
  } = context;

  switch (target) {
    case 'opponent':
      if (currentPlayer === 1) {
        setP2Life!(prev => Math.max(0, prev - damage));
      } else {
        setP1Life!(prev => Math.max(0, prev - damage));
      }
      addLog(`相手に${damage}ダメージ！`, 'damage');
      return true;

    case 'self':
      if (currentPlayer === 1) {
        setP1Life!(prev => Math.max(0, prev - damage));
      } else {
        setP2Life!(prev => Math.max(0, prev - damage));
      }
      addLog(`自分に${damage}ダメージ`, 'damage');
      return true;

    case 'self_monster': {
      const selfField = currentPlayer === 1 ? p1Field : p2Field;
      const setSelfField = currentPlayer === 1 ? setP1Field : setP2Field;
      const selfIndex = targetIndex !== null ? targetIndex : monsterIndex;

      if (selfIndex === null || selfIndex === undefined) {
        addLog('対象のモンスターが指定されていません', 'info');
        return false;
      }

      if (!selfField) {
        addLog('フィールドが取得できません', 'info');
        return false;
      }

      const targetMonster = selfField[selfIndex];
      if (!targetMonster) {
        addLog('対象のモンスターが存在しません', 'info');
        return false;
      }

      let newHp = Math.max(0, targetMonster.currentHp - damage);

      // 破壊耐性チェック: HP0になる場合はHP1で止まる
      if ((targetMonster as MonsterInstance & { indestructibleUntilEndOfTurn?: boolean }).indestructibleUntilEndOfTurn && newHp <= 0) {
        newHp = 1;
        addLog(`${targetMonster.name}に${damage}ダメージ！しかし破壊されない！（HP: ${newHp}）`, 'damage');
        setSelfField!(prev => prev.map((m, idx) => {
          if (idx === selfIndex && m) {
            return { ...m, currentHp: newHp };
          }
          return m;
        }));
        return true;
      }

      addLog(`${targetMonster.name}に${damage}ダメージ（残りHP: ${newHp}）`, 'damage');

      if (newHp <= 0) {
        // HP 0 以下なら破壊処理
        handleMonsterDestruction(context as any, targetMonster, selfIndex, currentPlayer as PlayerNumber);
      } else {
        // HP が残っているなら更新のみ
        setSelfField!(prev => prev.map((m, idx) => {
          if (idx === selfIndex && m) {
            return { ...m, currentHp: newHp };
          }
          return m;
        }));
      }
      return true;
    }

    case 'opponent_monster': {
      const opponentPlayer = currentPlayer === 1 ? 2 : 1;
      const opponentField = currentPlayer === 1 ? p2Field : p1Field;
      const setOpponentField = currentPlayer === 1 ? setP2Field : setP1Field;

      if (!opponentField) {
        addLog('相手フィールドが取得できません', 'info');
        return false;
      }

      // targetIndex がスロットインデックスかモンスターインデックスかを判断
      // targetIndex が null の場合は最初のモンスターを対象
      let targetMonster: MonsterInstance | null = null;
      let slotIndex: number | null = null;

      if (targetIndex !== null) {
        // targetIndex をスロットインデックスとして扱う
        if (opponentField[targetIndex]) {
          targetMonster = opponentField[targetIndex];
          slotIndex = targetIndex;
        } else {
          // スロットが空の場合、モンスターインデックスとして扱う
          const monsters = opponentField.map((m, idx) => m ? { monster: m, idx } : null).filter((x): x is { monster: MonsterInstance; idx: number } => x !== null);
          if (targetIndex < monsters.length) {
            targetMonster = monsters[targetIndex].monster;
            slotIndex = monsters[targetIndex].idx;
          }
        }
      } else {
        // 最初のモンスターを対象
        for (let i = 0; i < opponentField.length; i++) {
          if (opponentField[i]) {
            targetMonster = opponentField[i];
            slotIndex = i;
            break;
          }
        }
      }

      if (!targetMonster || slotIndex === null) {
        addLog('対象のモンスターがいません', 'info');
        return false;
      }

      // 【魔障壁】チェック: 相手の魔法効果を受けない
      if (isMashouhekiProtected(targetMonster, context as MagicEffectContext)) {
        logMashouhekiProtection(targetMonster, addLog);
        return false;
      }

      let newHp = Math.max(0, targetMonster.currentHp - damage);

      // 破壊耐性チェック: HP0になる場合はHP1で止まる
      if ((targetMonster as MonsterInstance & { indestructibleUntilEndOfTurn?: boolean }).indestructibleUntilEndOfTurn && newHp <= 0) {
        newHp = 1;
        addLog(`${targetMonster.name}に${damage}ダメージ！しかし破壊されない！（HP: ${newHp}）`, 'damage');
        setOpponentField!(prev => prev.map((m, idx) => {
          if (idx === slotIndex && m) {
            return { ...m, currentHp: newHp };
          }
          return m;
        }));
        return true;
      }

      addLog(`${targetMonster.name}に${damage}ダメージ（残りHP: ${newHp}）`, 'damage');

      if (newHp <= 0) {
        // HP 0 以下なら破壊処理
        handleMonsterDestruction(context as any, targetMonster, slotIndex, opponentPlayer as PlayerNumber);
      } else {
        // HP が残っているなら更新のみ
        setOpponentField!(prev => prev.map((m, idx) => {
          if (idx === slotIndex && m) {
            return { ...m, currentHp: newHp };
          }
          return m;
        }));
      }
      return true;
    }

    default:
      addLog('不明な対象です', 'info');
      return false;
  }
};

/**
 * デッキからカードをサーチ
 * @param context - ゲームコンテキスト
 * @param condition - 条件関数 (card) => boolean
 * @returns 見つかったカード
 */
export const searchCard = (context: EffectContext | TriggerContext, condition: (card: CardData) => boolean): CardData | null => {
  const {
    currentPlayer,
    p1Deck, p2Deck,
    setP1Deck, setP2Deck,
    setP1Hand, setP2Hand,
    addLog,
  } = context;

  const currentDeck = currentPlayer === 1 ? p1Deck : p2Deck;
  const setDeck = currentPlayer === 1 ? setP1Deck : setP2Deck;
  const setHand = currentPlayer === 1 ? setP1Hand : setP2Hand;

  if (!currentDeck) {
    addLog('デッキが取得できません', 'info');
    return null;
  }

  // デッキ内のカードデータ整合性チェック
  currentDeck.forEach((card, idx) => {
    if (card && !card.name) {
      console.warn('[effectHelpers.searchCard] デッキ内にnameがないカードがあります:', {
        index: idx,
        cardKeys: Object.keys(card),
        card,
      });
    }
  });

  const foundCard = currentDeck.find(condition);

  if (foundCard) {
    setDeck!(prev => prev.filter(c => (c as CardData & { uniqueId?: string }).uniqueId !== (foundCard as CardData & { uniqueId?: string }).uniqueId));
    setHand!(prev => [...prev, foundCard as HandCard]);
    addLog(`デッキから「${foundCard.name}」を手札に加えた`, 'info');
    return foundCard;
  } else {
    addLog('条件に合うカードが見つかりません', 'info');
    return null;
  }
};

/** 蘇生オプション */
interface ReviveOptions {
  attackHalf?: boolean;
  hpHalf?: boolean;
  fixedAttack?: number;
  fixedHp?: number;
  indestructible?: boolean;
}

/** 蘇生コンテキスト */
interface ReviveContext extends EffectContext {
  summoningSickness?: boolean;
}

/**
 * 墓地からカードを蘇生
 * @param context - ゲームコンテキスト
 * @param condition - 条件関数 (card) => boolean
 * @param options - 蘇生オプション
 *   - boolean: true = 攻撃力半減（後方互換性）
 *   - Object: {
 *       attackHalf: boolean,   // 攻撃力を半減するか
 *       hpHalf: boolean,       // HPを半減するか
 *       fixedAttack: number,   // 固定攻撃力（指定時は半減より優先）
 *       fixedHp: number,       // 固定HP（指定時は半減より優先）
 *       indestructible: boolean, // ターン終了時まで破壊されない
 *     }
 * @returns 成功したかどうか
 */
export const reviveFromGraveyard = (
  context: ReviveContext,
  condition: (card: CardData) => boolean,
  options: boolean | ReviveOptions = false
): boolean => {
  const {
    currentPlayer,
    p1Graveyard, p2Graveyard,
    setP1Graveyard, setP2Graveyard,
    p1Field, p2Field,
    setP1Field, setP2Field,
    addLog,
  } = context;

  // オプションを正規化（後方互換性: booleanの場合は攻撃力半減として扱う）
  const normalizedOptions: ReviveOptions = typeof options === 'boolean'
    ? { attackHalf: options, hpHalf: false, indestructible: false }
    : { attackHalf: false, hpHalf: false, indestructible: false, ...options };

  const currentGraveyard = currentPlayer === 1 ? p1Graveyard : p2Graveyard;
  const setCurrentGraveyard = currentPlayer === 1 ? setP1Graveyard : setP2Graveyard;
  const currentField = currentPlayer === 1 ? p1Field : p2Field;
  const setCurrentField = currentPlayer === 1 ? setP1Field : setP2Field;

  // 墓地から蘇生対象を検索
  const reviveCard = currentGraveyard.find(card => {
    return card.type === 'monster' && condition(card);
  });

  if (!reviveCard) {
    addLog('墓地に条件に合うモンスターが見つかりません', 'info');
    return false;
  }

  // 空きスロットを探す
  const emptySlotIndex = currentField.findIndex(slot => slot === null);
  if (emptySlotIndex === -1) {
    addLog('場が満杯で蘇生できません', 'info');
    return false;
  }

  // 蘇生（オプションに応じてステータス調整）
  setCurrentGraveyard(prev => prev.filter(c => (c as CardData & { uniqueId?: string }).uniqueId !== (reviveCard as CardData & { uniqueId?: string }).uniqueId));
  setCurrentField(prev => {
    const newField = [...prev];
    // 元のステータスを取得（墓地にある時点では元の値を保持）
    const originalAttack = reviveCard.attack || 0;
    const originalHp = reviveCard.hp || 0;

    // 攻撃力計算: 固定値 > 半減 > 元の値
    let finalAttack = originalAttack;
    if (normalizedOptions.fixedAttack !== undefined && normalizedOptions.fixedAttack !== null) {
      finalAttack = normalizedOptions.fixedAttack;
    } else if (normalizedOptions.attackHalf) {
      finalAttack = Math.floor(originalAttack / 2);
    }

    // HP計算: 固定値 > 半減 > 元の値
    let finalHp = originalHp;
    if (normalizedOptions.fixedHp !== undefined && normalizedOptions.fixedHp !== null) {
      finalHp = normalizedOptions.fixedHp;
    } else if (normalizedOptions.hpHalf) {
      finalHp = Math.floor(originalHp / 2);
    }

    const summoningSickness = context.summoningSickness ?? true;
    const revivedMonster: MonsterInstance & { indestructibleUntilEndOfTurn?: boolean; owner?: PlayerNumber } = {
      ...reviveCard,
      uniqueId: (reviveCard as CardData & { uniqueId?: string }).uniqueId || `${reviveCard.id}_${Date.now()}`,
      attack: finalAttack,
      currentAttack: finalAttack, // 戦闘計算用の現在攻撃力
      hp: finalHp,
      currentHp: finalHp,
      canAttack: !summoningSickness, // 召喚酔い設定に依存
      owner: currentPlayer, // 常時効果のターゲット判定用
      chargeCount: 0, // チャージカードをリセット
      statusEffects: [], // 状態異常をリセット
      originalCard: reviveCard,
      ...(normalizedOptions.indestructible && { indestructibleUntilEndOfTurn: true }),
    } as MonsterInstance & { indestructibleUntilEndOfTurn?: boolean; owner?: PlayerNumber };
    newField[emptySlotIndex] = revivedMonster as MonsterInstance;
    return newField;
  });

  // ログメッセージ生成
  const modifiers: string[] = [];
  if (normalizedOptions.fixedAttack !== undefined && normalizedOptions.fixedAttack !== null) {
    modifiers.push(`攻撃力${normalizedOptions.fixedAttack}`);
  } else if (normalizedOptions.attackHalf) {
    modifiers.push('攻撃力半減');
  }
  if (normalizedOptions.fixedHp !== undefined && normalizedOptions.fixedHp !== null) {
    modifiers.push(`HP${normalizedOptions.fixedHp}`);
  } else if (normalizedOptions.hpHalf) {
    modifiers.push('HP半減');
  }
  if (normalizedOptions.indestructible) {
    modifiers.push('ターン終了時まで破壊されない');
  }
  const modifierText = modifiers.length > 0 ? `（${modifiers.join('、')}）` : '';
  addLog(`${reviveCard.name}を墓地から蘇生！${modifierText}`, 'heal');
  return true;
};

/**
 * モンスターを破壊
 * @param context - ゲームコンテキスト
 * @param targetIndex - 対象のモンスターインデックス（opponent_monster用）
 * @param isOpponent - 相手のモンスターか
 * @returns 成功したかどうか
 */
export const destroyMonster = (context: EffectContext | TriggerContext, targetIndex: number | null, isOpponent: boolean = true): boolean => {
  const {
    currentPlayer,
    p1Field, p2Field,
    setP1Field, setP2Field,
    setP1Graveyard, setP2Graveyard,
    addLog,
  } = context;

  const targetField = isOpponent ? (currentPlayer === 1 ? p2Field : p1Field) : (currentPlayer === 1 ? p1Field : p2Field);
  const setTargetField = isOpponent ? (currentPlayer === 1 ? setP2Field : setP1Field) : (currentPlayer === 1 ? setP1Field : setP2Field);
  const setTargetGraveyard = isOpponent ? (currentPlayer === 1 ? setP2Graveyard : setP1Graveyard) : (currentPlayer === 1 ? setP1Graveyard : setP2Graveyard);

  if (!targetField) {
    addLog('フィールドが取得できません', 'info');
    return false;
  }

  const monsters = targetField.filter((m): m is MonsterInstance => m !== null);

  if (monsters.length === 0) {
    addLog('破壊する対象がいません', 'info');
    return false;
  }

  const targetMonster = targetIndex !== null && targetIndex < monsters.length ? monsters[targetIndex] : monsters[0];

  // 【魔障壁】チェック: 相手の魔法効果を受けない（相手モンスターを破壊する場合のみ）
  if (isOpponent && isMashouhekiProtected(targetMonster, context as MagicEffectContext)) {
    logMashouhekiProtection(targetMonster, addLog);
    return false;
  }

  // 破壊耐性チェック
  if ((targetMonster as MonsterInstance & { indestructibleUntilEndOfTurn?: boolean }).indestructibleUntilEndOfTurn) {
    addLog(`${targetMonster.name}は破壊されない！`, 'info');
    return false;
  }

  setTargetField!(prev => prev.map(m => {
    if (m && m.uniqueId === targetMonster.uniqueId) {
      return null;
    }
    return m;
  }));
  setTargetGraveyard!(prev => [...prev, targetMonster]);
  addLog(`${targetMonster.name}を破壊！`, 'damage');
  return true;
};

/**
 * カードをドロー
 * @param context - ゲームコンテキスト
 * @param count - ドロー枚数
 * @returns ドローしたカード配列
 */
export const drawCards = (context: EffectContext | TriggerContext, count: number = 1): CardData[] => {
  const {
    currentPlayer,
    p1Deck, p2Deck,
    setP1Deck, setP2Deck,
    setP1Hand, setP2Hand,
    addLog,
  } = context;

  const deck = currentPlayer === 1 ? p1Deck : p2Deck;
  const setDeck = currentPlayer === 1 ? setP1Deck : setP2Deck;
  const setHand = currentPlayer === 1 ? setP1Hand : setP2Hand;

  if (!deck || deck.length < count) {
    addLog(`デッキが足りません（残り${deck?.length ?? 0}枚）`, 'info');
    return [];
  }

  const drawnCards = deck.slice(0, count);
  setDeck!(prev => prev.slice(count));
  setHand!(prev => [...prev, ...drawnCards as HandCard[]]);
  addLog(`${count}枚ドロー！`, 'info');
  return drawnCards;
};

/**
 * ライフを回復
 * @param context - ゲームコンテキスト
 * @param amount - 回復量
 * @param isSelf - 自分か相手か
 * @returns 成功したかどうか
 */
export const healLife = (context: EffectContext | TriggerContext, amount: number, isSelf: boolean = true): boolean => {
  const {
    currentPlayer,
    setP1Life,
    setP2Life,
    addLog,
  } = context;

  if (isSelf) {
    if (currentPlayer === 1) {
      setP1Life!(prev => prev + amount);
    } else {
      setP2Life!(prev => prev + amount);
    }
    addLog(`ライフを${amount}回復！`, 'heal');
  } else {
    if (currentPlayer === 1) {
      setP2Life!(prev => prev + amount);
    } else {
      setP1Life!(prev => prev + amount);
    }
    addLog(`相手のライフを${amount}回復`, 'heal');
  }
  return true;
};

/** 攻撃力変更コンテキスト */
interface ModifyAttackContext extends EffectContext {
  monsterIndex?: number;
}

/**
 * モンスターの攻撃力を変更
 * @param context - ゲームコンテキスト
 * @param amount - 変更量（正の値でバフ、負の値でデバフ）
 * @param targetIndex - 対象のモンスターインデックス
 * @param isOpponent - 相手のモンスターか
 * @param permanent - 永続か一時的か
 * @returns 成功したかどうか
 */
export const modifyAttack = (
  context: ModifyAttackContext,
  amount: number,
  targetIndex: number | null = null,
  isOpponent: boolean = false,
  permanent: boolean = true
): boolean => {
  const {
    currentPlayer,
    p1Field, p2Field,
    setP1Field, setP2Field,
    monsterIndex,
    addLog,
  } = context;

  const targetField = isOpponent ? (currentPlayer === 1 ? p2Field : p1Field) : (currentPlayer === 1 ? p1Field : p2Field);
  const setTargetField = isOpponent ? (currentPlayer === 1 ? setP2Field : setP1Field) : (currentPlayer === 1 ? setP1Field : setP2Field);

  const useIndex = targetIndex !== null ? targetIndex : monsterIndex;

  if (useIndex === null || useIndex === undefined) {
    addLog('対象のモンスターが指定されていません', 'info');
    return false;
  }

  // 【魔障壁】チェック: 相手モンスターへのデバフ（魔法効果）の場合
  if (isOpponent && amount < 0) {
    const targetMonster = targetField[useIndex];
    if (targetMonster && isMashouhekiProtected(targetMonster, context as MagicEffectContext)) {
      logMashouhekiProtection(targetMonster, addLog);
      return false;
    }
  }

  setTargetField(prev => prev.map((m, idx) => {
    if (idx === useIndex && m) {
      const baseAttack = m.attack ?? m.currentAttack;
      const currentAtk = m.currentAttack !== undefined ? m.currentAttack : (m.attack ?? 0);
      const newAttack = Math.max(0, baseAttack + amount);
      const newCurrentAttack = Math.max(0, currentAtk + amount);
      const actionText = amount > 0 ? 'アップ' : 'ダウン';
      addLog(`${m.name}の攻撃力が${Math.abs(amount)}${actionText}！（攻撃力: ${newCurrentAttack}）`, amount > 0 ? 'heal' : 'damage');
      return { ...m, attack: newAttack, currentAttack: newCurrentAttack };
    }
    return m;
  }));
  return true;
};

/** HP変更コンテキスト */
interface ModifyHPContext extends EffectContext {
  monsterIndex?: number;
}

/**
 * モンスターのHPを変更
 * @param context - ゲームコンテキスト
 * @param amount - 変更量（正の値で増加、負の値で減少）
 * @param targetIndex - 対象のモンスターインデックス
 * @param isOpponent - 相手のモンスターか
 * @returns 成功したかどうか
 */
export const modifyHP = (
  context: ModifyHPContext,
  amount: number,
  targetIndex: number | null = null,
  isOpponent: boolean = false
): boolean => {
  const {
    currentPlayer,
    p1Field, p2Field,
    setP1Field, setP2Field,
    monsterIndex,
    addLog,
  } = context;

  const targetField = isOpponent ? (currentPlayer === 1 ? p2Field : p1Field) : (currentPlayer === 1 ? p1Field : p2Field);
  const setTargetField = isOpponent ? (currentPlayer === 1 ? setP2Field : setP1Field) : (currentPlayer === 1 ? setP1Field : setP2Field);

  const useIndex = targetIndex !== null ? targetIndex : monsterIndex;

  if (useIndex === null || useIndex === undefined) {
    addLog('対象のモンスターが指定されていません', 'info');
    return false;
  }

  // 【魔障壁】チェック: 相手モンスターへのデバフ（魔法効果）の場合
  if (isOpponent && amount < 0) {
    const targetMonster = targetField[useIndex];
    if (targetMonster && isMashouhekiProtected(targetMonster, context as MagicEffectContext)) {
      logMashouhekiProtection(targetMonster, addLog);
      return false;
    }
  }

  setTargetField(prev => prev.map((m, idx) => {
    if (idx === useIndex && m) {
      const newMaxHP = Math.max(1, (m.hp ?? m.currentHp) + amount);
      const newCurrentHP = Math.max(0, m.currentHp + amount);
      const actionText = amount > 0 ? 'アップ' : 'ダウン';
      addLog(`${m.name}のHPが${Math.abs(amount)}${actionText}！（HP: ${newCurrentHP}/${newMaxHP}）`, amount > 0 ? 'heal' : 'damage');
      return {
        ...m,
        hp: newMaxHP,
        currentHp: newCurrentHP
      };
    }
    return m;
  }));
  return true;
};

/** 墓地選択コンテキスト */
interface GraveyardSelectionContext extends EffectContext {
  setPendingGraveyardSelection?: React.Dispatch<React.SetStateAction<any>>;
  setShowGraveyardViewer?: React.Dispatch<React.SetStateAction<any>>;
}

/**
 * 墓地からカードを選択させる（モーダルUI表示）
 * @param context - ゲームコンテキスト
 * @param message - 選択を促すメッセージ
 * @param filter - カードをフィルタリングする条件関数 (card) => boolean
 * @param callback - 選択完了時のコールバック (selectedCard) => void
 * @param ownGraveyard - 自分の墓地か（デフォルトtrue、falseで相手の墓地）
 * @returns 選択UIを開始したかどうか
 */
export const selectFromGraveyard = (
  context: GraveyardSelectionContext,
  message: string,
  filter: ((card: CardData) => boolean) | null,
  callback: (selectedCard: CardData) => void,
  ownGraveyard: boolean = true
): boolean => {
  const {
    currentPlayer,
    p1Graveyard, p2Graveyard,
    setPendingGraveyardSelection,
    setShowGraveyardViewer,
    addLog,
  } = context;

  // 墓地選択機能がコンテキストに含まれていない場合
  if (!setPendingGraveyardSelection || !setShowGraveyardViewer) {
    addLog('墓地選択機能が利用できません', 'info');
    return false;
  }

  const targetPlayer = ownGraveyard ? currentPlayer : (currentPlayer === 1 ? 2 : 1);
  const graveyard = targetPlayer === 1 ? p1Graveyard : p2Graveyard;

  // 条件を満たすカードがあるか確認
  const selectableCards = filter ? graveyard.filter(filter) : graveyard;
  if (selectableCards.length === 0) {
    addLog('選択可能なカードが墓地にありません', 'info');
    return false;
  }

  // 墓地選択UIを開始
  setPendingGraveyardSelection({
    message,
    cards: selectableCards,
    count: 1,
    targetPlayer,
    filter,
    callback: (selectedCards: CardData[]) => {
      if (selectedCards[0]) callback(selectedCards[0]);
    },
    selectionOwner: currentPlayer, // 選択を行うべきプレイヤー（AI自動進行判定用）
  });
  setShowGraveyardViewer(targetPlayer);

  return true;
};

/** 蘇生選択コンテキスト */
interface ReviveSelectionContext extends EffectContext {
  summoningSickness?: boolean;
  setPendingGraveyardSelection?: React.Dispatch<React.SetStateAction<any>>;
  setShowGraveyardViewer?: React.Dispatch<React.SetStateAction<any>>;
}

/**
 * 墓地からモンスターを蘇生（選択UI付き）
 * @param context - ゲームコンテキスト
 * @param filter - カードをフィルタリングする条件関数
 * @param weakened - 弱体化状態で蘇生するか（攻撃力/HP半減）
 * @returns 蘇生UIを開始したかどうか
 */
export const selectAndReviveFromGraveyard = (
  context: ReviveSelectionContext,
  filter: ((card: CardData) => boolean) | null = null,
  weakened: boolean = false
): boolean => {
  const {
    currentPlayer,
    p1Field, p2Field,
    setP1Field, setP2Field,
    setP1Graveyard, setP2Graveyard,
    addLog,
    summoningSickness = false,
  } = context;

  const field = currentPlayer === 1 ? p1Field : p2Field;
  const setField = currentPlayer === 1 ? setP1Field : setP2Field;
  const setGraveyard = currentPlayer === 1 ? setP1Graveyard : setP2Graveyard;

  // 空きスロットを確認
  const emptySlotIndex = field.findIndex(slot => slot === null);
  if (emptySlotIndex === -1) {
    addLog('フィールドに空きがありません', 'info');
    return false;
  }

  // モンスターカードのみ、かつ追加フィルター
  const monsterFilter = (card: CardData): boolean => {
    if (card.type !== 'monster') return false;
    if (filter && !filter(card)) return false;
    return true;
  };

  return selectFromGraveyard(
    context,
    '蘇生するモンスターを選択してください',
    monsterFilter,
    (selectedCard: CardData) => {
      // 墓地から取り除く
      setGraveyard(prev => prev.filter(c => (c as CardData & { uniqueId?: string }).uniqueId !== (selectedCard as CardData & { uniqueId?: string }).uniqueId));

      // フィールドに配置
      const revivedMonster: MonsterInstance & { owner?: PlayerNumber } = {
        ...selectedCard,
        uniqueId: (selectedCard as CardData & { uniqueId?: string }).uniqueId || `${selectedCard.id}_${Date.now()}`,
        attack: weakened ? Math.floor((selectedCard.attack || 0) / 2) : (selectedCard.attack || 0),
        hp: weakened ? Math.floor((selectedCard.hp || 0) / 2) : (selectedCard.hp || 0),
        currentHp: weakened ? Math.floor((selectedCard.hp || 0) / 2) : (selectedCard.hp || 0),
        currentAttack: weakened ? Math.floor((selectedCard.attack || 0) / 2) : (selectedCard.attack || 0),
        canAttack: !summoningSickness, // 召喚酔い設定に依存
        chargeCount: 0,
        statusEffects: [],
        originalCard: selectedCard,
        owner: currentPlayer, // 常時効果のターゲット判定用
      } as MonsterInstance & { owner?: PlayerNumber };

      setField(prev => {
        const newField = [...prev];
        const slot = newField.findIndex(s => s === null);
        if (slot !== -1) {
          newField[slot] = revivedMonster as MonsterInstance;
        }
        return newField;
      });

      const weakenedText = weakened ? '（弱体化状態）' : '';
      addLog(`墓地から「${selectedCard.name}」を蘇生！${weakenedText}`, 'info');
    },
    true // 自分の墓地
  );
};

/**
 * 墓地からカードを手札に回収（選択UI付き）
 * @param context - ゲームコンテキスト
 * @param filter - カードをフィルタリングする条件関数
 * @returns 回収UIを開始したかどうか
 */
export const selectAndRecoverFromGraveyard = (
  context: GraveyardSelectionContext,
  filter: ((card: CardData) => boolean) | null = null
): boolean => {
  const {
    currentPlayer,
    setP1Hand, setP2Hand,
    setP1Graveyard, setP2Graveyard,
    addLog,
  } = context;

  const setHand = currentPlayer === 1 ? setP1Hand : setP2Hand;
  const setGraveyard = currentPlayer === 1 ? setP1Graveyard : setP2Graveyard;

  return selectFromGraveyard(
    context,
    '手札に加えるカードを選択してください',
    filter,
    (selectedCard: CardData) => {
      // 墓地から取り除く
      setGraveyard(prev => prev.filter(c => (c as CardData & { uniqueId?: string }).uniqueId !== (selectedCard as CardData & { uniqueId?: string }).uniqueId));

      // 手札に加える
      setHand!(prev => [...prev, selectedCard as HandCard]);

      addLog(`墓地から「${selectedCard.name}」を手札に加えた`, 'info');
    },
    true // 自分の墓地
  );
};

// ========================================
// 状態異常関連ヘルパー
// ========================================

/** 状態異常オプション */
interface StatusEffectOptions {
  duration?: number;
  removeChance?: number;
  value?: number;
  source?: string;
  sourceName?: string;
  expiresAfterEndPhases?: number;
  [key: string]: any;
}

/** applyStatus の戻り値型 */
interface ApplyStatusResult {
  success: boolean;
  message?: string;
  reason?: string;
}

/**
 * 相手モンスター1体に状態異常を付与（選択UI）
 * @param context - ゲームコンテキスト
 * @param statusType - 状態異常タイプ（STATUS_EFFECT_TYPES）
 * @param options - オプション（duration, removeChance, value等）
 * @param sourceName - 発動源の名前（ログ用）
 * @returns 成功したかどうか
 */
export const selectAndApplyStatusToOpponent = (
  context: MagicEffectContext,
  statusType: StatusEffectType,
  options: StatusEffectOptions = {},
  sourceName: string = ''
): boolean => {
  const {
    currentPlayer,
    p1Field, p2Field,
    setP1Field, setP2Field,
    addLog,
  } = context;

  const opponentField = currentPlayer === 1 ? p2Field : p1Field;
  const setOpponentField = currentPlayer === 1 ? setP2Field : setP1Field;

  // 相手モンスターがいるスロットを取得（魔障壁持ちを除外）
  const validTargets = opponentField
    .map((m, idx) => m ? idx : -1)
    .filter(idx => {
      if (idx === -1) return false;
      const monster = opponentField[idx];
      // 【魔障壁】チェック: 魔障壁持ちは対象から除外
      if (isMashouhekiProtected(monster, context)) {
        return false;
      }
      return true;
    });

  if (validTargets.length === 0) {
    // 全員が魔障壁持ちの場合も含む
    const hasMonsters = opponentField.some(m => m !== null);
    if (hasMonsters) {
      addLog('【魔障壁】対象のモンスターはすべて魔法の効果を受けない！', 'info');
    } else {
      addLog('相手の場にモンスターがいません', 'info');
    }
    return false;
  }

  // 状態異常を適用するヘルパー関数
  const applyStatus = (targetIdx: number) => {
    setOpponentField!(prev => prev.map((m, idx) => {
      if (idx === targetIdx && m) {
        const result = statusEffectEngine.applyStatus(m, statusType, {
          ...options,
          source: sourceName,
          sourceName: sourceName,
        }) as ApplyStatusResult;
        if (result.success) {
          addLog(`${m.name}を${getStatusDisplayName(statusType)}にした！`, 'damage');
        } else if (result.reason) {
          addLog(`${m.name}: ${result.reason}`, 'info');
        }
        return { ...m };
      }
      return m;
    }));
  };

  // 1体しかいない場合は自動選択
  if (validTargets.length === 1) {
    applyStatus(validTargets[0]);
    return true;
  }

  // 複数いる場合は選択UI表示
  const { setPendingMonsterTarget } = context;
  if (setPendingMonsterTarget) {
    const displayName = getStatusDisplayName(statusType);
    setPendingMonsterTarget({
      message: `${displayName}にするモンスターを選択`,
      targetPlayer: 'opponent',
      validTargets: validTargets,
      callback: (selectedIndex: number) => {
        applyStatus(selectedIndex);
      },
      selectionOwner: currentPlayer,
    });
  } else {
    // setPendingMonsterTargetがない場合（旧コンテキスト）は最初を選択
    applyStatus(validTargets[0]);
  }
  return true;
};

/**
 * 指定したインデックスの相手モンスターに状態異常を付与
 * @param context - ゲームコンテキスト
 * @param targetIndex - 対象のモンスターインデックス
 * @param statusType - 状態異常タイプ
 * @param options - オプション
 * @param sourceName - 発動源の名前
 * @returns 成功したかどうか
 */
export const applyStatusToOpponentMonster = (
  context: MagicEffectContext,
  targetIndex: number,
  statusType: StatusEffectType,
  options: StatusEffectOptions = {},
  sourceName: string = ''
): boolean => {
  const {
    currentPlayer,
    p1Field, p2Field,
    setP1Field, setP2Field,
    addLog,
  } = context;

  const opponentField = currentPlayer === 1 ? p2Field : p1Field;
  const setOpponentField = currentPlayer === 1 ? setP2Field : setP1Field;

  const target = opponentField[targetIndex];
  if (!target) {
    addLog('対象のモンスターが存在しません', 'info');
    return false;
  }

  // 【魔障壁】チェック: 相手の魔法効果を受けない
  if (isMashouhekiProtected(target, context)) {
    logMashouhekiProtection(target, addLog);
    return false;
  }

  setOpponentField!(prev => prev.map((m, idx) => {
    if (idx === targetIndex && m) {
      const result = statusEffectEngine.applyStatus(m, statusType, {
        ...options,
        source: sourceName,
        sourceName: sourceName,
      }) as ApplyStatusResult;
      if (result.success) {
        addLog(`${m.name}を${getStatusDisplayName(statusType)}にした！`, 'damage');
      }
      return { ...m };
    }
    return m;
  }));
  return true;
};

/**
 * 相手の全モンスターに状態異常を付与
 * @param context - ゲームコンテキスト
 * @param statusType - 状態異常タイプ
 * @param options - オプション
 * @param sourceName - 発動源の名前
 * @returns 付与したモンスター数
 */
export const applyStatusToAllOpponentMonsters = (
  context: MagicEffectContext,
  statusType: StatusEffectType,
  options: StatusEffectOptions = {},
  sourceName: string = ''
): number => {
  const {
    currentPlayer,
    p1Field, p2Field,
    setP1Field, setP2Field,
    addLog,
  } = context;

  const opponentField = currentPlayer === 1 ? p2Field : p1Field;
  const setOpponentField = currentPlayer === 1 ? setP2Field : setP1Field;

  let count = 0;
  let protectedCount = 0;

  setOpponentField!(prev => prev.map(m => {
    if (m) {
      // 【魔障壁】チェック: 魔障壁持ちはスキップ
      if (isMashouhekiProtected(m, context)) {
        protectedCount++;
        return m;
      }

      const result = statusEffectEngine.applyStatus(m, statusType, {
        ...options,
        source: sourceName,
        sourceName: sourceName,
      }) as ApplyStatusResult;
      if (result.success) {
        count++;
        addLog(`${m.name}を${getStatusDisplayName(statusType)}にした！`, 'damage');
      }
      return { ...m };
    }
    return m;
  }));

  // 魔障壁持ちがいた場合にログ出力
  if (protectedCount > 0) {
    addLog(`【魔障壁】${protectedCount}体は魔法の効果を受けなかった`, 'info');
  }

  return count;
};

/**
 * 自分のモンスターに状態異常を付与
 * @param context - ゲームコンテキスト
 * @param targetIndex - 対象のモンスターインデックス
 * @param statusType - 状態異常タイプ
 * @param options - オプション
 * @param sourceName - 発動源の名前
 * @returns 成功したかどうか
 */
export const applyStatusToOwnMonster = (
  context: EffectContext | TriggerContext,
  targetIndex: number,
  statusType: StatusEffectType,
  options: StatusEffectOptions = {},
  sourceName: string = ''
): boolean => {
  const {
    currentPlayer,
    p1Field, p2Field,
    setP1Field, setP2Field,
    addLog,
  } = context;

  const ownField = currentPlayer === 1 ? p1Field : p2Field;
  const setOwnField = currentPlayer === 1 ? setP1Field : setP2Field;

  if (!ownField) {
    addLog('フィールド情報を取得できません', 'info');
    return false;
  }

  const target = ownField[targetIndex];
  if (!target) {
    addLog('対象のモンスターが存在しません', 'info');
    return false;
  }

  setOwnField!(prev => prev.map((m, idx) => {
    if (idx === targetIndex && m) {
      const result = statusEffectEngine.applyStatus(m, statusType, {
        ...options,
        source: sourceName,
        sourceName: sourceName,
      }) as ApplyStatusResult;
      if (result.success) {
        addLog(`${m.name}を${getStatusDisplayName(statusType)}にした！`, 'info');
      }
      return { ...m };
    }
    return m;
  }));
  return true;
};

/**
 * 自分のモンスターを覚醒状態にする
 * - 攻撃力が上昇する
 * - ターン終了時に解除される（duration: 0）
 * @param context - ゲームコンテキスト
 * @param targetIndex - 対象のモンスターインデックス
 * @param atkBonus - 攻撃力上昇量
 * @param sourceName - 発動源の名前
 * @returns 成功したかどうか
 */
export const applyAwakening = (
  context: EffectContext | TriggerContext,
  targetIndex: number,
  atkBonus: number,
  sourceName: string = ''
): boolean => {
  const {
    currentPlayer,
    p1Field, p2Field,
    setP1Field, setP2Field,
    addLog,
  } = context;

  const ownField = currentPlayer === 1 ? p1Field : p2Field;
  const setOwnField = currentPlayer === 1 ? setP1Field : setP2Field;

  if (!ownField) {
    addLog('フィールド情報を取得できません', 'info');
    return false;
  }

  const target = ownField[targetIndex];
  if (!target) {
    addLog('対象のモンスターが存在しません', 'info');
    return false;
  }

  // 既に覚醒状態かチェック
  if (statusEffectEngine.hasStatus(target, STATUS_EFFECT_TYPES.AWAKENED)) {
    addLog(`${target.name}は既に覚醒状態です`, 'info');
    return false;
  }

  setOwnField!(prev => prev.map((m, idx) => {
    if (idx === targetIndex && m) {
      // statusEffects配列を新しくコピーしてから状態異常を追加（イミュータブル更新）
      const newStatusEffects = m.statusEffects ? [...m.statusEffects] : [];

      // 既に覚醒状態がある場合は上書き（重複防止）
      const existingIndex = newStatusEffects.findIndex(s => s.type === STATUS_EFFECT_TYPES.AWAKENED);
      if (existingIndex !== -1) {
        newStatusEffects.splice(existingIndex, 1);
      }

      // 新しい覚醒状態を追加
      const statusEffect = {
        id: `${STATUS_EFFECT_TYPES.AWAKENED}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: STATUS_EFFECT_TYPES.AWAKENED as StatusEffectType,
        source: sourceName,
        sourceName: sourceName,
        appliedTurn: 0,
        duration: 0, // ターン終了時に解除
        removeChance: 0,
        removeOnDamage: false,
        usageCount: 0,
        maxUsage: -1,
        value: atkBonus,
        stackable: false,
      };
      newStatusEffects.push(statusEffect as any);

      addLog(`${m.name}が覚醒した！攻撃力が${atkBonus}アップ！`, 'damage');

      return {
        ...m,
        statusEffects: newStatusEffects,
      };
    }
    return m;
  }));
  return true;
};

/** 覚醒選択コンテキスト */
interface AwakeningSelectionContext extends EffectContext {
  setPendingMonsterTarget?: React.Dispatch<React.SetStateAction<any>>;
}

/**
 * 対象のモンスターを選択して覚醒状態にする（UI選択付き）
 * @param context - ゲームコンテキスト
 * @param condition - 対象モンスターの条件関数 (monster) => boolean
 * @param atkBonus - 攻撃力上昇量
 * @param sourceName - 発動源の名前
 * @returns 選択モードを開始したかどうか
 */
export const selectAndApplyAwakening = (
  context: AwakeningSelectionContext,
  condition: (monster: MonsterInstance) => boolean,
  atkBonus: number,
  sourceName: string = ''
): boolean => {
  const {
    currentPlayer,
    p1Field, p2Field,
    setPendingMonsterTarget,
    addLog,
  } = context;

  const ownField = currentPlayer === 1 ? p1Field : p2Field;

  // 条件を満たすモンスターがいるかチェック
  const validTargets = ownField.map((m, idx) => ({ monster: m, index: idx }))
    .filter((item): item is { monster: MonsterInstance; index: number } => item.monster !== null && condition(item.monster as MonsterInstance));

  if (validTargets.length === 0) {
    addLog('対象となるモンスターがいません', 'info');
    return false;
  }

  // 1体しかいない場合は自動選択
  if (validTargets.length === 1) {
    return applyAwakening(context, validTargets[0].index, atkBonus, sourceName);
  }

  // 複数いる場合は選択モード
  if (setPendingMonsterTarget) {
    setPendingMonsterTarget({
      message: '覚醒させるモンスターを選択してください',
      targetPlayer: 'self',
      condition: condition,
      callback: (selectedIndex: number) => {
        applyAwakening(context, selectedIndex, atkBonus, sourceName);
      },
      selectionOwner: currentPlayer, // 選択を行うべきプレイヤー（AI自動進行判定用）
    });
  }

  return true;
};

/**
 * 相手フィールドの全モンスターに一時的な攻撃力ダウンを付与
 * エンドフェイズ回数で解除される（「次のターン終了時まで」= 2）
 * @param context - ゲームコンテキスト
 * @param value - 攻撃力ダウン値（正の値で指定）
 * @param endPhases - 何回のエンドフェイズ後に解除されるか（「次のターン終了時まで」= 2）
 * @param sourceName - 発動源の名前
 * @returns 付与に成功したモンスター数
 */
export const applyTemporaryAtkDownToAllOpponents = (
  context: MagicEffectContext,
  value: number,
  endPhases: number,
  sourceName: string = ''
): number => {
  const {
    currentPlayer,
    p1Field, p2Field,
    setP1Field, setP2Field,
    addLog,
  } = context;

  const opponentField = currentPlayer === 1 ? p2Field : p1Field;
  const setOpponentField = currentPlayer === 1 ? setP2Field : setP1Field;

  let count = 0;
  let protectedCount = 0;

  setOpponentField!(prev => prev.map(m => {
    if (m) {
      // 【魔障壁】チェック: 魔障壁持ちはスキップ
      if (isMashouhekiProtected(m, context)) {
        protectedCount++;
        return m;
      }

      const result = statusEffectEngine.applyStatus(m, STATUS_EFFECT_TYPES.ATK_DOWN, {
        value: value,
        expiresAfterEndPhases: endPhases,
        source: sourceName,
        sourceName: sourceName,
      }) as ApplyStatusResult;
      if (result.success) {
        count++;
        addLog(`${m.name}の攻撃力が${value}下がった！（${endPhases === 2 ? '次のターン終了時まで' : `${endPhases}エンドフェイズ後まで`}）`, 'info');
      }
      return { ...m };
    }
    return m;
  }));

  // 魔障壁持ちがいた場合にログ出力
  if (protectedCount > 0) {
    addLog(`【魔障壁】${protectedCount}体は魔法の効果を受けなかった`, 'info');
  }

  return count;
};

// ========================================
// 状態異常の一括処理（ターン開始・エンドフェイズ）
// ========================================

/** ターン処理コンテキスト */
interface TurnProcessContext {
  setP1Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP2Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  addLog: (message: string, type?: 'info' | 'damage' | 'heal') => void;
}

/**
 * 両プレイヤーのフィールドに対して状態異常のターン開始時処理を実行
 * - 確率解除判定（眠り、凍結など）
 * - 寄生のATK減少
 * @param context - ゲームコンテキスト
 */
export const processStatusEffectsTurnStart = (context: TurnProcessContext): void => {
  const {
    setP1Field, setP2Field,
    addLog,
  } = context;

  const processField = (setField: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>) => {
    setField(prev => prev.map(m => {
      if (!m) return null;
      const result = statusEffectEngine.processTurnStart(m) as ProcessTurnStartResult;

      // 状態異常解除をログ出力
      if (result.removedEffects.length > 0) {
        result.removedEffects.forEach(effect => {
          addLog(`${m.name}の${getStatusDisplayName(effect.type)}が解除された！`, 'info');
        });
      }

      // 寄生によるATK減少をログ出力
      if (result.parasiteAtkReduction > 0) {
        addLog(`${m.name}は寄生により攻撃力-${result.parasiteAtkReduction}！（${result.monster.currentAttack}）`, 'damage');
      }

      return result.monster;
    }));
  };

  processField(setP1Field);
  processField(setP2Field);
};

/**
 * 両プレイヤーのフィールドに対して状態異常のエンドフェイズ時処理を実行
 * - 寄生の効果無効解除（相手エンドフェイズで解除）
 * @param context - ゲームコンテキスト
 * @param currentPlayer - 現在のターンプレイヤー
 */
export const processStatusEffectsEndPhase = (context: TurnProcessContext, currentPlayer: PlayerNumber): void => {
  const {
    setP1Field, setP2Field,
    addLog,
  } = context;

  const processField = (setField: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>) => {
    setField(prev => prev.map(m => {
      if (!m) return m;
      const result = statusEffectEngine.processOpponentEndPhase(m, currentPlayer) as ProcessOpponentEndPhaseResult;

      // 寄生の効果無効解除をログ出力
      if (result.parasiteEffectNegationRemoved) {
        addLog(`${m.name}の効果無効化が解除された（寄生効果）`, 'info');
      }

      return result.monster;
    }));
  };

  processField(setP1Field);
  processField(setP2Field);
};
